import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlayCircle, MousePointerClick, ArrowRight } from "lucide-react";

/**
 * FirstTimeWelcome — the modal that greets a business owner the very first
 * time they land on the dashboard for a newly-created business. Shows
 * three next-step CTAs: guided tour, Business Academy, or "explore on my own".
 * Persists dismissal so it never shows again for that business.
 */
export default function FirstTimeWelcome({ business, onStartTour }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!business?.business_id) return;
    let cancelled = false;
    axios
      .get(`${API}/academy/progress/${business.business_id}`,
           { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        // Only open when the owner has NEVER dismissed and hasn't started/completed the tour.
        if (!r.data.welcome_dismissed_at && r.data.tour_status === "not_started") {
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [business?.business_id]);

  const dismiss = async () => {
    setOpen(false);
    if (!business?.business_id) return;
    try {
      await axios.post(
        `${API}/academy/progress/${business.business_id}/welcome-dismiss`,
        {},
        { withCredentials: true },
      );
    } catch {
      /* silent */
    }
  };

  const startTour = () => {
    dismiss();
    onStartTour?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent
        className="sm:max-w-md bg-tbc-navy text-white border-tbc-gold/30"
        data-testid="first-time-welcome-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-tbc-gold">
            Welcome to Your TBC Business Dashboard
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/80 font-body text-sm leading-relaxed mt-1">
          Let&apos;s get your business ready to be discovered. Pick how you want to start:
        </p>
        <div className="space-y-2 mt-4">
          <Button
            onClick={startTour}
            className="w-full justify-start bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full h-11"
            data-testid="welcome-start-tour-btn"
          >
            <MousePointerClick className="w-4 h-4 mr-2" />
            Start Guided Setup <ArrowRight className="w-4 h-4 ml-auto" />
          </Button>
          <Link to="/business-academy" onClick={dismiss}>
            <Button
              variant="outline"
              className="w-full justify-start border-white/25 text-white bg-transparent hover:bg-white/10 rounded-full h-11"
              data-testid="welcome-academy-btn"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Watch Setup Tutorials <ArrowRight className="w-4 h-4 ml-auto" />
            </Button>
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="w-full text-white/50 hover:text-white/80 text-sm py-2"
            data-testid="welcome-explore-btn"
          >
            I&apos;ll explore on my own →
          </button>
        </div>
        <p className="text-white/40 text-xs mt-2 text-center">
          You can restart the tour any time from Business Academy.
        </p>
      </DialogContent>
    </Dialog>
  );
}
